import React, { useEffect, useMemo, useRef, useState } from 'react';
import { func, number, shape } from 'prop-types';
import axios from 'axios';
import { AutoComplete, Checkbox, Col, DatePicker, Form, Input, Radio, Row, Select, Upload, message } from 'antd';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';

import FormWrapper from '../formWrapper';
import {
  RESOURCE_FIELDS_LIST,
  YEAR_FIELDS,
  MONTH_FIELDS,
  FIELD_DEPENDENCIES,
  getDisabledStepTwoFields,
  getStepTwoFieldKey,
  getStepTwoInitialFiles,
  getStepTwoInitialValues,
  getStepTwoInitialLocationValues,
  getStepTwoValidationRules,
} from './util';
import './style.scss';

const { TextArea } = Input;
const { Option } = Select;

function StepTwo({ form, profileData, registerValuesChangeHandler, profileInformationForm, totalSteps, displayStep }) {
  const [resourceOptions, setResourceOptions] = useState({});
  const [, setLocationValues] = useState({});
  const [disabledFields, setDisabledFields] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const lastPrefillKey = useRef(null);

  const fields = profileInformationForm?.fields || [];

  const visibleFields = useMemo(
    () =>
      [...fields]
        .filter((field) => !field.is_hidden)
        .sort((firstField, secondField) => (firstField.display_order || 0) - (secondField.display_order || 0)),
    [fields],
  );

  useEffect(() => {
    const profileKey = `${profileData?.id || 'anonymous'}-${fields.length}`;
    if (isEmpty(profileData) || !fields.length || lastPrefillKey.current === profileKey) {
      return;
    }

    const nextValues = getStepTwoInitialValues(profileData, fields);
    const nextLocationValues = getStepTwoInitialLocationValues(profileData, fields);
    const nextUploadedFiles = getStepTwoInitialFiles(profileData, fields);
    form.setFieldsValue({ ...nextValues, locationValues: nextLocationValues });
    setLocationValues(nextLocationValues);
    setUploadedFiles(nextUploadedFiles);
    setDisabledFields(getDisabledStepTwoFields(nextValues));
    lastPrefillKey.current = profileKey;
  }, [profileData, fields, form]);

  useEffect(() => {
    const timeoutIds = [];
    const autocompleteInstances = [];

    visibleFields.forEach((field) => {
      const fieldKey = getStepTwoFieldKey(field);
      if (field.type !== 'location') {
        return;
      }

      const timeoutId = setTimeout(() => {
        const input = document.getElementById(fieldKey);
        if (!input || !window.google?.maps?.places) {
          return;
        }

        const places = new window.google.maps.places.Autocomplete(input);
        places.setFields(['geometry', 'formatted_address', 'name', 'place_id']);
        const listener = window.google.maps.event.addListener(places, 'place_changed', () => {
          const googlePlace = places.getPlace();
          const value = {
            gpid: googlePlace.place_id,
            name: googlePlace.formatted_address,
          };
          setLocationValues((currentLocationValues) => {
            const nextLocationValues = { ...currentLocationValues, [fieldKey]: value };
            form.setFieldsValue({
              [fieldKey]: googlePlace.formatted_address,
              locationValues: nextLocationValues,
            });
            return nextLocationValues;
          });
        });
        autocompleteInstances.push({ places, listener });
      }, 500);

      timeoutIds.push(timeoutId);
    });

    return () => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));

      if (!window.google?.maps?.event) {
        return;
      }

      autocompleteInstances.forEach(({ places, listener }) => {
        if (listener) {
          window.google.maps.event.removeListener(listener);
          return;
        }

        if (places) {
          window.google.maps.event.clearInstanceListeners(places);
        }
      });
    };
  }, [visibleFields, form]);

  const handleFileChange = (file, field) => {
    const fieldKey = getStepTwoFieldKey(field);

    if (file.status === 'removed') {
      const nextUploadedFiles = { ...uploadedFiles };
      delete nextUploadedFiles[fieldKey];
      setUploadedFiles(nextUploadedFiles);
      form.setFieldsValue({
        [fieldKey]: undefined,
        files: nextUploadedFiles,
      });
      return;
    }

    const nextUploadedFiles = { ...uploadedFiles, [fieldKey]: file };
    setUploadedFiles(nextUploadedFiles);
    form.setFieldsValue({
      [fieldKey]: fieldKey,
      files: nextUploadedFiles,
    });
  };

  const handleValuesChange = (changedValues, allValues) => {
    const nextDisabledFields = getDisabledStepTwoFields(allValues);
    const fieldsToClear = {};

    nextDisabledFields
      .filter((fieldPath) => !disabledFields.includes(fieldPath))
      .forEach((fieldPath) => {
        fieldsToClear[fieldPath] = undefined;
      });

    if (!isEmpty(fieldsToClear)) {
      form.setFieldsValue(fieldsToClear);
    }

    setDisabledFields(nextDisabledFields);

    Object.keys(changedValues).forEach((changedFieldPath) => {
      Object.entries(FIELD_DEPENDENCIES).forEach(([fieldPath, config]) => {
        if (config.triggerFields.includes(changedFieldPath)) {
          setTimeout(() => {
            form.validateFields([fieldPath]).catch(() => {});
          }, 0);
        }
      });
    });
  };

  useEffect(() => {
    if (!registerValuesChangeHandler) {
      return undefined;
    }

    registerValuesChangeHandler(handleValuesChange);
    return () => registerValuesChangeHandler(null);
  });

  const getOptionValue = (option) => {
    if (typeof option === 'string') {
      return option;
    }

    return option.id || option.value || option.label || option.text || option.name;
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'string') {
      return option;
    }

    return option.label || option.text || option.name || option.value;
  };

  const handleResourceSearch = async (value, field) => {
    const fieldKey = `${field.entity_path || ''}__${field.path}`;
    const resource = RESOURCE_FIELDS_LIST[fieldKey];
    if (!resource || !value) {
      return;
    }

    try {
      const searchParams = new URLSearchParams({
        q: value,
        r: resource,
      });
      const response = await axios.get(`/resources/autofilllist?${searchParams.toString()}`);
      setResourceOptions((currentOptions) => ({
        ...currentOptions,
        [fieldKey]: response.data.map((option) => option.name),
      }));
    } catch (error) {
      setResourceOptions((currentOptions) => ({
        ...currentOptions,
        [fieldKey]: [],
      }));
    }
  };

  // eslint-disable-next-line complexity
  const renderFieldInput = (field) => {
    const fieldKey = getStepTwoFieldKey(field);
    const isDisabled = disabledFields.includes(fieldKey);

    if (RESOURCE_FIELDS_LIST[fieldKey]) {
      return (
        <AutoComplete
          size="large"
          disabled={isDisabled}
          options={(resourceOptions[fieldKey] || []).map((option) => ({ value: option }))}
          onSearch={(value) => handleResourceSearch(value, field)}
        />
      );
    }

    if (YEAR_FIELDS.includes(fieldKey)) {
      return <DatePicker picker="year" className="w-full" size="large" disabled={isDisabled} />;
    }

    if (MONTH_FIELDS.includes(fieldKey)) {
      return <DatePicker picker="month" format="MMM" className="w-full" size="large" disabled={isDisabled} />;
    }

    switch (field.type) {
      case 'textarea':
        return <TextArea rows={6} className="mentorship-onboarding-input" disabled={isDisabled} />;
      case 'select':
        return (
          <Select size="large" disabled={isDisabled}>
            {(field.options || []).map((option) => (
              <Option key={getOptionValue(option)} value={getOptionValue(option)}>
                {getOptionLabel(option)}
              </Option>
            ))}
          </Select>
        );
      case 'multi_select':
        return (
          <Select size="large" mode="multiple" disabled={isDisabled}>
            {(field.options || []).map((option) => (
              <Option key={getOptionValue(option)} value={getOptionValue(option)}>
                {getOptionLabel(option)}
              </Option>
            ))}
          </Select>
        );
      case 'radio':
        return (
          <Radio.Group disabled={isDisabled}>
            {(field.options || []).map((option) => (
              <Radio key={getOptionValue(option)} value={getOptionValue(option)}>
                {getOptionLabel(option)}
              </Radio>
            ))}
          </Radio.Group>
        );
      case 'checkbox':
        return (
          <Checkbox.Group disabled={isDisabled}>
            {(field.options || []).map((option) => (
              <Checkbox key={getOptionValue(option)} value={getOptionValue(option)}>
                {getOptionLabel(option)}
              </Checkbox>
            ))}
          </Checkbox.Group>
        );
      case 'date':
        return <DatePicker size="large" className="w-full" disabled={isDisabled} />;
      case 'number':
        return <Input type="number" size="large" className="mentorship-onboarding-input" disabled={isDisabled} />;
      case 'email':
        return <Input type="email" size="large" className="mentorship-onboarding-input" disabled={isDisabled} />;
      case 'file': {
        const currentFileList = uploadedFiles[fieldKey] ? [uploadedFiles[fieldKey]] : [];
        const isProfilePictureField = field.path === 'present_picture';

        return (
          <Upload
            multiple={false}
            listType="picture-card"
            accept={
              isProfilePictureField
                ? '.jpg, .jpeg, .png, .gif, .webp'
                : '.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx'
            }
            beforeUpload={(file) => {
              const fileSize = (file.size || 1) / 1024 / 1024;
              if (fileSize > 7) {
                message.error('File size should be less than 7 MB');
                return false;
              }
              return true;
            }}
            customRequest={({ onSuccess }) => {
              setTimeout(() => {
                onSuccess?.('ok');
              }, 0);
            }}
            showUploadList={{ showPreviewIcon: false, showDownloadIcon: false }}
            onChange={({ file }) => handleFileChange(file, field)}
            fileList={currentFileList}
            disabled={isDisabled}>
            <If condition={!currentFileList.length}>
              <button type="button" className="mentorship-step-two-upload-btn">
                <FontAwesomeIcon icon={faPlusCircle} />
                <div style={{ marginTop: 8 }}>Upload</div>
              </button>
            </If>
          </Upload>
        );
      }
      case 'location':
        return <Input id={fieldKey} size="large" className="mentorship-onboarding-input" disabled={isDisabled} />;
      case 'url':
        return <Input type="url" size="large" className="mentorship-onboarding-input" disabled={isDisabled} />;
      default:
        return <Input size="large" className="mentorship-onboarding-input" disabled={isDisabled} />;
    }
  };

  return (
    <div className="mentorship-step-two" aria-live="polite">
      <FormWrapper
        totalSteps={totalSteps}
        step={displayStep}
        header={
          <h3 className="arc-H300">
            <I18nCustomFormatter id="step-2-title" />
          </h3>
        }>
        <div className="step-two-content-wrapper">
          <p className="arc-support arc-color-gray8 mb16">
            Complete the information below to continue with mentorship onboarding.
          </p>
          <div className="arc-support arc-color-gray7 mb24">
            <span className="arc-color-red6">*</span> Indicates a required field
          </div>

          <Row gutter={20}>
            {visibleFields.map((field) => {
              if (field.type === 'section') {
                return (
                  <Col span={24} key={field.id}>
                    <div className="mentorship-step-two-section-heading">{field.label}</div>
                  </Col>
                );
              }

              const normalizedFieldKey = getStepTwoFieldKey(field);
              const span = ['first_name', 'last_name'].includes(field.path) ? 12 : 24;

              return (
                <Col span={span} key={field.id}>
                  <Form.Item
                    label={
                      <span className="mentorship-step-two-field-label">
                        {/* {field.required ? <span className="arc-color-red6 mr4">*</span> : null} */}
                        {field.label}
                      </span>
                    }
                    tooltip={field.help_text}
                    name={normalizedFieldKey}
                    rules={getStepTwoValidationRules(field, form, visibleFields)}>
                    {renderFieldInput(field)}
                  </Form.Item>
                </Col>
              );
            })}
          </Row>
        </div>
      </FormWrapper>
    </div>
  );
}

StepTwo.propTypes = {
  form: shape().isRequired,
  profileData: shape(),
  registerValuesChangeHandler: func,
  profileInformationForm: shape(),
  totalSteps: number.isRequired,
  displayStep: number,
};

StepTwo.defaultProps = {
  profileData: {},
  registerValuesChangeHandler: null,
  profileInformationForm: { fields: [] },
  displayStep: 2,
};

export default StepTwo;
